import { Children, cloneElement, isValidElement, useId } from "react";

/*
 * Field — the labelled-control primitive.
 *
 * Every form in this app used to render the label as a *sibling* of its
 * control with no `htmlFor` and no `id`, so nothing associated the two:
 * screen readers announced 48 inputs as "edit text, blank". Rather than
 * hand-thread ids through every call site, this generates one with
 * useId and injects it into the child.
 *
 * It also wires up the states the design system already has CSS for but
 * nothing was using — `.brutal-hint`, `.brutal-error`, and
 * `.brutal-input[aria-invalid="true"]` were all dead rules.
 *
 * Usage:
 *   <Field label="Title" hint="Shown on the share page" error={errors.title}>
 *     <input className="brutal-input" value={...} onChange={...} />
 *   </Field>
 *
 * For a set of related controls (radios, checkboxes) there is no single
 * child to label, so pass `group` and it renders a real fieldset/legend:
 *   <Field label="How are you creating this demo?" group>…</Field>
 */
export default function Field({
  label,
  hint,
  error,
  required = false,
  group = false,
  className = "",
  labelExtra = null,
  children,
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  if (group) {
    // A legend names the whole group; individual controls keep their own
    // labels. `min-w-0` because fieldset defaults to min-content width and
    // will otherwise refuse to shrink inside a grid.
    return (
      <fieldset className={`min-w-0 border-0 p-0 m-0 ${className}`}>
        <legend className="brutal-label p-0">
          {label}
          {required && <RequiredMark />}
          {labelExtra}
        </legend>
        {children}
        {hint && <p className="brutal-hint">{hint}</p>}
        {error && <p className="brutal-error" role="alert">{error}</p>}
      </fieldset>
    );
  }

  // Inject onto the single control child. Anything else (a wrapper, a
  // fragment, multiple children) is passed through untouched so this can
  // be adopted incrementally without breaking a call site.
  const child = Children.only(children);
  const control = isValidElement(child)
    ? cloneElement(child, {
        id: child.props.id ?? id,
        "aria-describedby": child.props["aria-describedby"] ?? describedBy,
        "aria-invalid": error ? "true" : child.props["aria-invalid"],
        required: child.props.required ?? (required || undefined),
      })
    : child;

  return (
    <div className={className}>
      <label className="brutal-label" htmlFor={child.props.id ?? id}>
        {label}
        {required && <RequiredMark />}
        {labelExtra}
      </label>
      {control}
      {hint && <p className="brutal-hint" id={hintId}>{hint}</p>}
      {error && <p className="brutal-error" id={errorId} role="alert">{error}</p>}
    </div>
  );
}

/* Required is conveyed three ways: the native attribute (for validation),
   the asterisk (visual), and the word (for screen readers) — never by the
   asterisk alone, which is announced inconsistently across ATs. */
function RequiredMark() {
  return (
    <>
      <span aria-hidden="true" className="text-ink-accent ml-1">*</span>
      <span className="sr-only"> (required)</span>
    </>
  );
}
